import cx from "classnames";
import React, { useState } from "react";
import { useAppService } from "../hooks/use-app-service";
import I18 from "./atoms/i18";
import UIcon from "./uicon-component";
import { OptionSelectorModal } from "./compound/option-selector";

/**
 * Filter component.
 *
 * @param {string} selectedOption - The currently selected option.
 * @param {Function} setSelectedOption - A function to set the selected option.
 * @param {Array} options - An array of option objects with keys and labels.
 */
interface Props {
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  setSortDuration?: (duration: string | undefined) => void;
  options: {
    key: string;
    label: string;
    icon: any;
    isCommunityTopic?: boolean;
    bgColor?: string;
    textColor?: string;
  }[];
  removeSelectedCommunityTopicFilter: () => void;
}

const Filter: React.FC<Props> = ({
  selectedOption,
  setSelectedOption,
  setSortDuration,
  options,
  removeSelectedCommunityTopicFilter,
}) => {
  const { analyticsService } = useAppService();

  const [trendingSelectorActive, setTrendingSelectorActive] = useState<boolean>();
  const [selectedTrendingOption, setSelectedTrendingOption] = useState<{ label: string, value: string }>();

  return (
    <div className="flex gap-2.5 overflow-x-auto w-fit" style={{ scrollbarWidth: "none" }}>
      {options.map((option) => (
        <button
          key={option.key}
          className={cx(
            "flex cursor-pointer items-center justify-center gap-1.5 rounded-full border border-border px-2 py-1 text-xs transition-all duration-200 ease-in-out",
            {
              "cursor-pointer bg-primary text-primary-foreground":
                selectedOption === option.key,
              "text-secondary-foreground/70 focus-within:scale-[.95] hover:border-secondary-foreground/50 hover:bg-accent/90 hover:text-secondary-foreground/90":
                selectedOption !== option.key,
            }
          )}
          style={{
            minWidth: "112px",
            backgroundColor: option.bgColor,
            color: option.textColor,
          }}
          onClick={() => {
            if (option.key === "trending") {
              // trigger duration selector
              setTrendingSelectorActive(true);
              return;
            }
            if (selectedOption !== option.key) {
              setSelectedOption(option.key);
              setSelectedTrendingOption(undefined);
              analyticsService.track("post-filter", {
                filterName: option.key,
              });
            }
          }}>
          {option.icon && <UIcon icon={option.icon} className="mr-1" />}
          <I18>
            {option.label}
            {option.key === "trending" && selectedTrendingOption ? ":" + selectedTrendingOption.label : ""}
            {option.key === "trending" ? <UIcon icon="caret-down" className=" mt-0.5" /> : <></>}
          </I18>
          {option.isCommunityTopic && (
            <div onClick={removeSelectedCommunityTopicFilter}>
              <UIcon icon="cross" className="ml-2" />
            </div>
          )}{" "}
        </button>
      ))}
      <OptionSelectorModal
        active={trendingSelectorActive}
        setActive={setTrendingSelectorActive}
        options={[
          { label: "24 Hrs", value: "24-hours" },
          { label: "1 Wk", value: "1-week" },
          { label: "1 Mn", value: "1-month" },
          { label: "1 Yr", value: "1-year" },
          { label: "All", value: "" },
        ]}
        selectedOption={selectedTrendingOption}
        setSelectedOption={setSelectedTrendingOption}
        onChange={option => {
          setSelectedTrendingOption(option);
          setTrendingSelectorActive(false);
          setSortDuration && setSortDuration(option?.value);
          setSelectedOption("trending");
        }}
      />
    </div>
  );
};

export default Filter;
